// src/context/NotificationContext.tsx

import React, { createContext, useCallback, useContext, useState } from 'react';
import { FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiInfo } from 'react-icons/fi';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface Notification {
  id: number;
  message: string;
  type: NotificationType;
}

interface NotificationContextType {
  addNotification: (message: string, type: NotificationType) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification debe ser usado dentro de un NotificationProvider');
  }
  return context;
};

const NotificationItem: React.FC<{ notification: Notification; onClose: (id: number) => void }> = ({
  notification,
  onClose
}) => {
  const getColorAndIcon = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return { color: '#4CAF50', Icon: FiCheckCircle };
      case 'error':
        return { color: '#F44336', Icon: FiAlertCircle };
      case 'info':
        return { color: '#2196F3', Icon: FiInfo };
      case 'warning':
        return { color: '#F59E0B', Icon: FiAlertTriangle };
      default:
        return { color: '#9E9E9E', Icon: FiInfo };
    }
  };

  const { color, Icon } = getColorAndIcon(notification.type);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose(notification.id);
    }, 5000);
    return () => clearTimeout(timer);
  }, [notification.id, onClose]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'white',
        borderLeft: `5px solid ${color}`,
        borderRadius: '8px',
        padding: '15px',
        marginBottom: '10px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        minWidth: '300px',
        maxWidth: '420px',
        transition: 'opacity 0.3s, transform 0.3s',
        animation: 'slideIn 0.3s forwards'
      }}
    >
      <Icon style={{ color: color, marginRight: '10px', fontSize: '1.2rem' }} />
      <span
        style={{
          color: '#333',
          flexGrow: 1,
          fontSize: '0.95rem',
          whiteSpace: 'pre-line'
        }}
      >
        {notification.message}
      </span>
      <button
        onClick={() => onClose(notification.id)}
        style={{
          background: 'none',
          border: 'none',
          color: '#999',
          cursor: 'pointer',
          fontSize: '1rem',
          marginLeft: '10px'
        }}
        aria-label="Cerrar notificación"
        type="button"
      >
        &times;
      </button>
    </div>
  );
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [idCounter, setIdCounter] = useState(0);

  const addNotification = useCallback((message: string, type: NotificationType) => {
    const id = Date.now() + idCounter;
    setIdCounter(prev => prev + 1);
    const newNotification: Notification = { id, message, type };
    setNotifications(prev => [newNotification, ...prev]);
  }, [idCounter]);

  const removeNotification = useCallback((id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const contextValue: NotificationContextType = {
    addNotification
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <div
        id="notification-root"
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999
        }}
      >
        {notifications.map(n => (
          <NotificationItem key={n.id} notification={n} onClose={removeNotification} />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
